import { Clipboard } from '@angular/cdk/clipboard';
import { finalize, Subject, takeUntil } from 'rxjs';
import {
  Component,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from '@angular/core';

// Model
import { Comment, Memo } from 'shared/models';

// Service
import {
  APIError,
  CommentAPIService,
  ReadingGroupMembersAPIService,
  SharedUserService,
} from 'shared/services';
import { AlertService, DialogService, ToastService } from 'shared/ui';
import { BookService } from '../../services/book.service';
import { UIStateService } from '../../services/ui-state.service';
import { EventBusService } from '../../services/event-bus.service';
import { AnnotationsV2Service } from '../../services/annotations-v2.service';
import { OnlineReadingMemberService } from '../../services/online-reading-member.service';

// Dialog
import { MemoDialogComponent } from '../../dialogs/memo-dialog/memo-dialog.component';
import { MemoReportDialogComponent } from '../../dialogs/memo-report-dialog/memo-report-dialog.component';
import { MemoExportDialogComponent } from '../../dialogs/memo-export-dialog/memo-export-dialog.component';
import { MemoSharingDialogComponent } from '../../dialogs/memo-sharing-dialog/memo-sharing-dialog.component';
import { UserDetailsDialogComponent } from '../../dialogs/user-details-dialog/user-details-dialog.component';
import { CommentatorDetailsDialogComponent } from '../../dialogs/commentator-details-dialog/commentator-details-dialog.component';

// Component
import { MemoType } from '../memo/memo.component';
import { SidebarContainerComponent } from '../sidebar-container/sidebar-container.component';
import { MemoCopyDialogComponent } from '../../dialogs/memo-copy-dialog/memo-copy-dialog.component';
import { CommentReportDialogComponent } from '../../dialogs/comment-report-dialog/comment-report-dialog.component';
import { ReadingGroupMembersDialogComponent } from '../../dialogs/reading-group-members-dialog/reading-group-members-dialog.component';
import { CrossBrowsing } from '../../constants/cross-browsing';

function createCopyText(memo: Memo): string {
  return `${memo.text}\n:${memo.content}`;
}

@Component({
  selector: 'viewer-comment-right-panel',
  templateUrl: './comment-right-panel.component.html',
  styleUrls: ['./comment-right-panel.component.scss'],
})
export class CommentRightPanelComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public _isOpened = false;
  private _unsubscriber = new Subject<void>();

  private _groupId?: number;
  private _commentaryId?: number;
  private _bid?: string;

  public _memo?: Memo;
  public _comments: Comment[] = [];
  public _total: number = 0;
  public _hiddenCount: number = 0; // 숨김/차단된 댓글 개수
  public _selectedCommentId: string = ''; // 댓글에 답글을 달기위해 선택된 댓글 id값
  public _selectedCommentIdForChange: string = ''; // 댓글, 답글 수정을 위해 선택된 댓글 id값
  public _selectedReplyIdForChange: string = ''; // 답글 수정을 위해 선택된 답글 id값
  public _inputFormType:
    | 'comment'
    | 'reply'
    | 'comment-change'
    | 'reply-change'
    | 'alert'
    | 'initial-comment' = 'initial-comment'; // 댓글 입력 폼 타입
  public _selectedCommentForMenu: Comment | null = null; // 메뉴에서 선택된 댓글
  public _selectedCommentForMoreButton: Comment | null = null; // 더보기 버튼에서 선택된 댓글 (_selectedCommentForMenu에 넣기위한 임시 데이터 저장소)
  public _selectedCommentForChange: string = '';

  public _isAuthorOnline = false;

  public _inputValue: string = '';
  public _changeInputValue: string = '';

  private _isLikeRequesting = false;

  private _alertSelectedCommentId?: string;

  private retryCount = 0; // 알림함을 통해 댓글로 오는 경우 관련 로직 최대 수행횟수
  private _dialogParams?: {
    bid: string;
    userId: string;
    isUserBlocked: boolean;
    groupId: number;
  };

  public _openedFrom:
    | 'memo-list'
    | 'reading-mode'
    | 'reading-mode-small-popup'
    | 'user-details'
    | 'alert' = 'memo-list';

  public _commentMetion = true; // 댓글에서 답글 다는 경우 true, 답글에서 답글 다는 경우 false

  @ViewChild('nicknameElement', { static: false }) nicknameElement?: ElementRef;
  @ViewChild('contentContainer', { static: false })
  contentContainer?: ElementRef;

  constructor(
    private _clipboard: Clipboard,
    private _bookService: BookService,
    private _toastService: ToastService,
    private _alertService: AlertService,
    private _dialogService: DialogService,
    private _userService: SharedUserService,
    private _uiStateService: UIStateService,
    private _eventBusService: EventBusService,
    private _commentApiService: CommentAPIService,
    private _annotationsService: AnnotationsV2Service,
    private _sidebarContainerComponent: SidebarContainerComponent,
    private _onlineReadingMemberService: OnlineReadingMemberService,
    private _readingGroupMembersAPIService: ReadingGroupMembersAPIService
  ) {}

  ngOnInit(): void {
    // 메모 댓글 버튼 클릭 이벤트 구독
    this._eventBusService
      .on('MemoListComponent:commentButtonClick')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ memo, source, dialogParams, cId }) => {
        this._memo = memo;
        this._openedFrom = source;
        this.inputFormType = 'initial-comment';
        this._dialogParams = dialogParams;

        if (source === 'alert') {
          this._alertSelectedCommentId = cId;
        }

        this._updateAuthorOnlineStatus();

        this._groupId = this._annotationsService.getState().groupId;
        this._commentaryId = this._annotationsService.getState().commentaryId;
        this._bid = this._bookService.book?.meta.bid;

        this._commentApiService
          .getComments(this._memo.id)
          .subscribe((result) => {
            if (result) {
              this._total = result.total;
              this._hiddenCount = result.hidden_count;
              this._comments = result.items;
              this._scrollToMemo(this._alertSelectedCommentId!);
            }
          });
        this.open();
      });

    this._onlineReadingMemberService.onlineStatusChange$
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data) => {
        if (this._memo && data.userId === this._memo.author.id) {
          this._isAuthorOnline = data.isOnline;
        }
      });

    // 읽기 모드 변경 이벤트 구독 - 패널 닫기
    this._eventBusService
      .on('ReadingModeDialogComponent:groupChanged')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        // 읽기 모드가 변경되면 패널을 닫음
        if (this.isOpened()) {
          this._closeCommentPanel();
        }
      });

    // 페이지 전환 시 댓글 입력창 비활성화
    this._eventBusService
      .on('ContentsComponent:addressChange')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        // 댓글 입력창이 활성화되어 있으면 비활성화
        if (
          this._inputFormType === 'comment' ||
          this._inputFormType === 'reply' ||
          this._inputFormType === 'comment-change' ||
          this._inputFormType === 'reply-change'
        ) {
          this.inputFormType = 'initial-comment';
          this._inputValue = '';
          this._changeInputValue = '';
        }
      });
  }

  ngAfterViewInit(): void {
    if (this.contentContainer) {
      this.contentContainer.nativeElement.addEventListener('scroll', () => {
        this._closeAllMenus();
      });
    }
  }

  ngOnDestroy(): void {
    this._unsubscriber.next();
    this._unsubscriber.complete();
  }

  set inputFormType(
    value:
      | 'alert'
      | 'comment'
      | 'reply'
      | 'comment-change'
      | 'reply-change'
      | 'initial-comment'
  ) {
    this._inputFormType = value;

    const textarea = document.querySelector(
      'textarea[data-component="comment-right-panel"]'
    ) as HTMLTextAreaElement;

    if (value === 'initial-comment') {
      textarea.blur();
      return;
    }

    // 댓글 입력 폼이 활성화될 때만 포커스
    setTimeout(() => {
      const activeTextarea = document.querySelector(
        'textarea[data-component="comment-right-panel"]'
      ) as HTMLTextAreaElement;
      activeTextarea.focus({ preventScroll: true });
    }, 0);
  }

  get inputFormType(): string {
    return this._inputFormType;
  }

  get _singleMode(): boolean {
    return this._groupId == null;
  }

  get type(): MemoType {
    if (this._memo?.is_mine) {
      return 'mine';
    }

    return this._singleMode ? 'commentary' : 'group';
  }

  close(): void {
    this._sidebarContainerComponent.close('comment-right');
    this._isOpened = false;

    if (this._dialogParams) {
      this._dialogService.open(ReadingGroupMembersDialogComponent, {
        data: this._dialogParams,
      });
    }
  }

  open(): void {
    this._isOpened = true;
    this._sidebarContainerComponent.open('comment-right');

    // memo-popup 닫기 이벤트 발생
    this._eventBusService.fire('CommentRightPanelComponent:opened');
  }

  isOpened(): boolean {
    return (
      this._sidebarContainerComponent.isOpened('comment-right') &&
      this._isOpened
    );
  }

  // 댓글, 답글 입력 이벤트
  onInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    let value = target.value;

    // 10자 초과 시 바로 잘라내기
    if (value.length > 200) {
      value = value.substring(0, 200);
      // UI에 바로 반영하기 위해 input의 value를 직접 변경
      target.value = value;
    }

    // 최종적으로 잘린 값을 변수에 저장
    this._inputValue = value;
  }

  // 키보드 이벤트 처리
  onKeyUp(event: any, formType: string): void {
    event.stopPropagation();

    // 터치 디바이스에서는 Enter 키로 입력 안되도록 처리
    if (CrossBrowsing.touchDevice) {
      return;
    }

    if (!this._inputValue.trim()) {
      return; // 빈 내용이면 실행하지 않음
    }

    switch (formType) {
      case 'comment':
        this._onSubmitButtonClick();
        break;
      case 'comment-change':
        if (this._inputValue !== this._changeInputValue) {
          this._onChangeCommentButtonClick();
        }
        break;
      case 'reply':
        this._onSubmitReplyButtonClick();
        break;
      case 'reply-change':
        if (this._inputValue !== this._changeInputValue) {
          this._onChangeReplyButtonClick();
        }
        break;
    }

    // 키보드 이벤트 처리 후 즉시 리턴하여 버튼 클릭 이벤트와 중복 실행 방지
    return;
  }

  onKeyDown(event: KeyboardEvent, formType: string): void {
    event.stopPropagation();

    // ESC 키 처리: 입력 취소
    if (event.key === 'Escape') {
      event.preventDefault();
      this._onCancelButtonClick();
      return;
    }

    // 엔터키 누를 때 기본 동작(줄바꿈) 방지
    if (event.key === 'Enter' && !CrossBrowsing.touchDevice) {
      event.preventDefault();

      if (!this._inputValue.trim()) {
        return; // 빈 내용이면 실행하지 않음
      }

      if (event.isComposing) {
        return;
      }

      switch (formType) {
        case 'comment':
          this._onSubmitButtonClick();
          break;
        case 'comment-change':
          if (this._inputValue !== this._changeInputValue) {
            this._onChangeCommentButtonClick();
          }
          break;
        case 'reply':
          this._onSubmitReplyButtonClick();
          break;
        case 'reply-change':
          if (this._inputValue !== this._changeInputValue) {
            this._onChangeReplyButtonClick();
          }
          break;
      }
    }
  }

  // 알림함을 통해 댓글로 오는 경우 스크롤 로직
  private _scrollToMemo(commentId: string): void {
    const existingFocusedMemos = document.querySelectorAll('div.focused-memo');
    existingFocusedMemos.forEach((memo) => {
      memo.classList.remove('focused-memo');
    });
    // 해당 메모 요소 찾기
    const memoElement = document.querySelector(
      `div[data-comment-id="${commentId}"]`
    );

    if (this.retryCount > 10) {
      return;
    }

    if (!memoElement) {
      this.retryCount++;
      setTimeout(() => {
        this._scrollToMemo(commentId);
      }, 100 * this.retryCount);
    }

    if (memoElement) {
      // 스크롤 가능한 부모 컨테이너 찾기 (.content-container 클래스)
      const scrollContainer = memoElement.closest('.content-container');

      if (scrollContainer) {
        // 컨테이너와 메모 요소의 위치 계산
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = memoElement.getBoundingClientRect();

        // 컨테이너의 현재 스크롤 위치
        const currentScrollTop = scrollContainer.scrollTop;

        // 메모를 컨테이너 중앙에 위치시키기 위한 스크롤 계산
        const elementOffsetFromContainer = elementRect.top - containerRect.top;
        const targetScrollTop =
          currentScrollTop +
          elementOffsetFromContainer -
          containerRect.height / 2 +
          elementRect.height / 2;

        // 부드러운 스크롤로 이동
        scrollContainer.scrollTo({
          top: targetScrollTop,
        });
      } else {
        // 컨테이너를 찾지 못한 경우 기본 동작
        memoElement.scrollIntoView({
          block: 'center',
          inline: 'nearest',
        });
      }
    }
  }

  // 댓글 메뉴 오픈 이벤트
  _onCommentMenuOpen(comment: Comment): void {
    this._selectedCommentForMoreButton = comment;
  }

  // 댓글 신고 이벤트
  _onReportCommentMenuClick(): void {
    if (!this._memo) {
      return;
    }

    this._selectedCommentForMenu = this._selectedCommentForMoreButton;

    this._dialogService
      .open(CommentReportDialogComponent, {
        data: {
          authorName: this._selectedCommentForMenu!.author.nickname,
          authorAvatarImageURL:
            this._selectedCommentForMenu!.author.avatar_image_url,
          memoId: this._memo!.id,
          commentId: this._selectedCommentForMenu!.id,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.blocked) {
          this._eventBusService.fire('UserBlocked:refreshMemoLists');
          this.close();
        }
      });
  }

  // 댓글 숨기기 이벤트
  _onBlockCommentMenuClick(): void {
    this._alertService
      .openConfirm(
        undefined,
        '선택한 댓글을 숨김 처리합니다. 숨김 처리 후에는 다시 볼 수 없습니다. 계속하시겠습니까?',
        '계속하기'
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          if (!this._memo) {
            return;
          }

          this._selectedCommentForMenu = this._selectedCommentForMoreButton;

          this._commentApiService
            .blockComment(this._memo.id, this._selectedCommentForMenu!.id)
            .subscribe(() => {
              const selectedCommentCount = this._comments.find(
                (comment) => comment.id === this._selectedCommentForMenu!.id
              )!.replies.length;

              this._comments = this._comments.filter(
                (comment) => comment.id !== this._selectedCommentForMenu!.id
              );

              this._hiddenCount += selectedCommentCount + 1;
            });
        }
      });
  }

  // 답글 숨기기 이벤트
  _onBlockReplyMenuClick(): void {
    this._alertService
      .openConfirm(
        undefined,
        '선택한 답글을 숨김 처리합니다. 숨김 처리 후에는 다시 볼 수 없습니다. 계속하시겠습니까?',
        '계속하기'
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          if (!this._memo) {
            return;
          }

          this._selectedCommentForMenu = this._selectedCommentForMoreButton;

          this._commentApiService
            .blockComment(this._memo.id, this._selectedCommentForMenu!.id)
            .subscribe(() => {
              const comment = this._comments.find(
                (comment) =>
                  comment.id === this._selectedCommentForMenu!.comment_id
              );
              if (comment) {
                comment.replies = comment.replies.filter(
                  (reply) => reply.id !== this._selectedCommentForMenu!.id
                );

                this._hiddenCount += 1;
              }
            });
        }
      });
  }

  // 댓글 추천 이벤트
  _onLikeCommentButtonClick(commentId: string): void {
    if (!this._memo) {
      return;
    }

    const targetComment = this._comments.find(
      (comment) => comment.id === commentId
    );

    if (targetComment?.is_liked === 1) {
      this._commentApiService
        .unRecommendComment(this._memo.id, commentId)
        .subscribe(() => {
          const delta = targetComment!.is_liked === 1 ? -1 : 1;

          if (targetComment) {
            targetComment.like_count += delta;
            targetComment.is_liked = delta > 0 ? 1 : 0;
          }
        });
    } else {
      this._commentApiService
        .recommendComment(this._memo.id, commentId)
        .subscribe(() => {
          const delta = targetComment!.is_liked === 1 ? -1 : 1;

          if (targetComment) {
            targetComment.like_count += delta;
            targetComment.is_liked = delta > 0 ? 1 : 0;
          }
        });
    }
  }

  // 답글 추천 이벤트
  _onLikeReplyButtonClick(commentId: string, replyId: string): void {
    if (!this._memo) {
      return;
    }

    const targetComment = this._comments.find(
      (comment) => comment.id === commentId
    );

    const targetReply = targetComment!.replies.find(
      (reply) => reply.id === replyId
    );

    if (targetReply?.is_liked === 1) {
      this._commentApiService
        .unRecommendComment(this._memo.id, replyId)
        .subscribe(() => {
          const delta = targetReply!.is_liked === 1 ? -1 : 1;

          if (targetReply) {
            targetReply.like_count += delta;
            targetReply.is_liked = delta > 0 ? 1 : 0;
          }
        });
    } else {
      this._commentApiService
        .recommendComment(this._memo.id, replyId)
        .subscribe(() => {
          const delta = targetReply!.is_liked === 1 ? -1 : 1;

          if (targetReply) {
            targetReply.like_count += delta;
            targetReply.is_liked = delta > 0 ? 1 : 0;
          }
        });
    }
  }

  // 뒤로가기 버튼 클릭 이벤트
  _onBackButtonClick(): void {
    if (this._inputValue !== '') {
      const dialogRef = this._alertService.openConfirm(
        undefined,
        '작성 중인 내용이 저장되지 않았습니다. <br/> 계속하시겠습니까?',
        '계속하기'
      );

      dialogRef.afterClosed().subscribe((ok) => {
        if (ok) {
          // 이전 메모로 스크롤 이동 이벤트 발생
          this._eventBusService.fire(
            'CommentRightPanelComponent:scrollToMemo',
            {
              memoId: this._memo!.id,
              source: this._openedFrom,
            }
          );
          this._closeCommentPanel();
        }
      });
    } else {
      this._eventBusService.fire('CommentRightPanelComponent:scrollToMemo', {
        memoId: this._memo!.id,
        source: this._openedFrom,
      });
      this._closeCommentPanel();
    }
  }

  // 메모 댓글 닫기 이벤트
  _onCloseButtonClick(): void {
    if (this._inputValue !== '') {
      const dialogRef = this._alertService.openConfirm(
        undefined,
        '작성 중인 내용이 저장되지 않았습니다. <br/> 계속하시겠습니까?',
        '계속하기'
      );

      dialogRef.afterClosed().subscribe((ok) => {
        if (ok) {
          if (
            this._openedFrom !== 'reading-mode-small-popup' &&
            this._openedFrom !== 'user-details'
          ) {
            this._eventBusService.fire(
              'CommentRightPanelComponent:scrollToMemo',
              {
                memoId: this._memo!.id,
                source: this._openedFrom,
              }
            );
          }

          this._closeCommentPanel();
        }
      });
    } else {
      if (
        this._openedFrom !== 'reading-mode-small-popup' &&
        this._openedFrom !== 'user-details'
      ) {
        this._eventBusService.fire('CommentRightPanelComponent:scrollToMemo', {
          memoId: this._memo!.id,
          source: this._openedFrom,
        });
      }

      this._closeCommentPanel();
    }
  }

  // 댓글 패널 닫기 로직
  private _closeCommentPanel(): void {
    this._inputValue = '';
    this.inputFormType = 'initial-comment';
    this._selectedCommentId = '';
    this._selectedCommentIdForChange = '';
    this._selectedReplyIdForChange = '';
    this._selectedCommentForMenu = null;
    this.close();
  }

  // 댓글 생성 이벤트
  _onSubmitButtonClick(): void {
    if (!this._memo) {
      return;
    }

    if (this._inputValue.trim() === '') {
      return;
    }

    this._commentApiService
      .createComment(this._memo.id, this._inputValue.trim(), '')
      .subscribe(
        (result) => {
          this.inputFormType = 'initial-comment';
          this._inputValue = '';
          this._comments.push(result);
          this._total++;
          this._memo!.comment_count++;
          this._selectedCommentForChange = '';

          // 댓글 생성 후 해당 댓글로 스크롤 이동
          setTimeout(() => {
            this._scrollToElement(result.id);
          }, 100);
        },
        (error) => {
          if (error.status === 404) {
            this._toastService.openWarning('존재하지 않는 메모, 댓글입니다.');
          }
        }
      );
  }

  // 답글 생성 이벤트
  _onSubmitReplyButtonClick(): void {
    if (!this._memo) {
      return;
    }

    if (this._inputValue.trim() === '') {
      return;
    }

    this._selectedCommentForMenu = this._selectedCommentForMoreButton;

    this._commentApiService
      .createReply(
        this._memo.id,
        this._inputValue.trim(),
        this._selectedCommentForMenu?.is_mine
          ? ''
          : this._selectedCommentForMenu?.author.id || '',
        this._selectedCommentId
      )
      .subscribe(
        (result) => {
          this._inputValue = '';
          this.inputFormType = 'initial-comment';
          this._comments = this._comments.map((comment) => {
            if (comment.id === result.comment_id) {
              comment.replies.push(result);
              this._selectedCommentForChange = '';

              this._total++;
              this._memo!.comment_count++;

              // 답글 생성 후 해당 답글로 스크롤 이동
              setTimeout(() => {
                this._scrollToElement(result.id);
              }, 100);

              return comment;
            }

            return comment;
          });
        },
        (error) => {
          if (error.status === 404) {
            this._toastService.openWarning('존재하지 않는 메모, 댓글입니다.');
          }
        }
      );
  }

  // 댓글 수정 이벤트
  _onChangeCommentButtonClick(): void {
    if (!this._memo) {
      return;
    }

    if (this._inputValue.trim() === '') {
      return;
    }

    this._commentApiService
      .updateComment(
        this._memo.id,
        this._inputValue.trim(),
        '',
        this._selectedCommentIdForChange
      )
      .subscribe(
        (result) => {
          this.inputFormType = 'initial-comment';
          this._inputValue = '';
          this._comments = this._comments.map((comment) => {
            this._selectedCommentForChange = '';

            if (comment.id === this._selectedCommentIdForChange) {
              return {
                ...comment,
                content: result.content,
                updated_at: result.updated_at,
              };
            }

            return comment;
          });
        },
        (error) => {
          if (error.status === 404) {
            this._toastService.openWarning('존재하지 않는 메모, 댓글입니다.');
          }
        }
      );
  }

  // 답글 수정 이벤트
  _onChangeReplyButtonClick(): void {
    if (!this._memo) {
      return;
    }

    if (this._inputValue.trim() === '') {
      return;
    }

    this._selectedCommentForMenu = this._selectedCommentForMoreButton;

    this._commentApiService
      .updateReply(
        this._memo.id,
        this._inputValue.trim(),
        this._selectedCommentForMenu?.mention?.id || '',
        this._selectedReplyIdForChange
      )
      .subscribe(
        (result) => {
          this.inputFormType = 'initial-comment';
          this._inputValue = '';
          this._comments = this._comments.map((comment) => {
            this._selectedCommentForChange = '';

            if (comment.id === this._selectedCommentIdForChange) {
              const updatedReplies = comment.replies.map((reply) => {
                if (reply.id === this._selectedReplyIdForChange) {
                  return {
                    ...reply,
                    content: result.content,
                    updated_at: result.updated_at,
                  };
                }

                return reply;
              });

              return { ...comment, replies: updatedReplies };
            }

            return comment;
          });
        },
        (error) => {
          if (error.status === 404) {
            this._toastService.openWarning('존재하지 않는 메모, 댓글입니다.');
          }
        }
      );
  }

  // 댓글 삭제 이벤트
  _onDeleteCommentButtonClick(): void {
    const dialogRef = this._alertService.openConfirm(
      undefined,
      '댓글을 삭제하시겠습니까?',
      '삭제하기'
    );

    dialogRef.afterClosed().subscribe((ok) => {
      if (ok) {
        this._selectedCommentForMenu = this._selectedCommentForMoreButton;

        if (!this._memo) {
          return;
        }

        if (!this._selectedCommentForMenu) {
          return;
        }

        this._commentApiService
          .deleteComment(this._memo.id, this._selectedCommentForMenu.id)
          .subscribe(() => {
            const repliesCount = this._comments.find(
              (comment) => comment.id === this._selectedCommentForMenu!.id
            )!.replies.length;

            this._comments = this._comments.filter(
              (comment) => comment.id !== this._selectedCommentForMenu!.id
            );
            this._memo!.comment_count -= repliesCount + 1;
            this._total -= repliesCount + 1;
          });
      }
    });
  }

  // 답글 삭제 이벤트
  _onDeleteReplyButtonClick(): void {
    const dialogRef = this._alertService.openConfirm(
      undefined,
      '댓글을 삭제하시겠습니까?',
      '삭제하기'
    );

    dialogRef.afterClosed().subscribe((ok) => {
      if (ok) {
        this._selectedCommentForMenu = this._selectedCommentForMoreButton;

        if (!this._memo) {
          return;
        }

        this._commentApiService
          .deleteComment(this._memo.id, this._selectedCommentForMenu!.id)
          .subscribe(() => {
            const targetComment = this._comments.find(
              (comment) =>
                comment.id === this._selectedCommentForMenu!.comment_id
            );
            if (targetComment) {
              targetComment.replies = targetComment.replies.filter(
                (reply) => reply.id !== this._selectedCommentForMenu!.id
              );
              this._memo!.comment_count--;
              this._total--;
            }
          });
      }
    });
  }

  // 메모 작성 박스 오픈 이벤트
  _openCommentInputBox(): void {
    this._selectedCommentId = '';
    this._selectedCommentForChange = '';
    this._inputValue = '';
    this.inputFormType = 'comment';
  }

  // 답글 작성 박스 오픈 이벤트
  _openReplyInputBox(commentId: string, replyId?: string): void {
    if (replyId) {
      this._selectedCommentForChange = replyId;
    } else {
      this._selectedCommentForChange = commentId;
    }
    this._selectedCommentId = commentId;
    this._inputValue = '';
    this.inputFormType = 'reply';

    this._selectedCommentForMenu = this._comments.find(
      (comment) => comment.id === commentId
    )!;
    this._selectedCommentForMoreButton = this._selectedCommentForMenu;

    if (replyId) {
      this._selectedCommentForMenu = this._selectedCommentForMenu!.replies.find(
        (comment) => comment.id === replyId
      )!;

      this._selectedCommentForMoreButton = this._selectedCommentForMenu;
    }
  }

  // 메모 수정 박스 오픈 이벤트
  _openCommentChangeInputBox(): void {
    this.inputFormType = 'comment-change';
  }

  // 답글 수정 박스 오픈 이벤트
  _openReplyChangeInputBox(): void {
    this.inputFormType = 'reply-change';
  }

  // 댓글, 답글 수정중 취소 버튼 클릭 이벤트
  _onCancelButtonClick(): void {
    // 답글 입력 취소 이벤트 발생
    if (
      this._inputFormType === 'reply' ||
      this._inputFormType === 'reply-change'
    ) {
      this._eventBusService.fire(
        'ReadingGroupMemoListComponent:replyInputCancelled' as any
      );
    }
    // 댓글 입력 취소 이벤트 발생
    else if (
      this._inputFormType === 'comment' ||
      this._inputFormType === 'comment-change'
    ) {
      this._eventBusService.fire(
        'ContentsComponent:commentInputCancelled' as any
      );
    }

    this._selectedCommentForChange = '';
    this.inputFormType = 'initial-comment';
    this._inputValue = '';
  }

  // 메모 공유 이벤트
  _onShareButtonClick(): void {
    const user = this._userService.getUser();

    if (!user) {
      return;
    }

    if (!this._memo) {
      return;
    }

    this._dialogService.open(MemoSharingDialogComponent, {
      data: {
        memo: this._memo,
        author: {
          nickname: user.nickname,
          avatar_image_url: user.avatar_image_url,
        },
      },
    });
  }

  // 메모 수정 이벤트
  _onEditMenuClick(memo: Memo): void {
    if (
      this._uiStateService.isCommentaryEditor &&
      !this._uiStateService.canEditCommentary
    ) {
      this._toastService.openWarning(
        '판매 설정이 완료된 코멘터리는 수정이 불가합니다.'
      );

      return;
    }

    this._dialogService
      .open(MemoDialogComponent, {
        data: {
          data: {
            id: memo.id,
            text: memo.text,
            content: memo.content,
            pinned: memo.pinned_at != null,
          },
          type: 'memoEdit',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.deleted) {
          this._eventBusService.fire('MemoListComponent:memoDeleted', {
            memoId: this._memo!.id,
          });
          this.close();
        } else if (result?.updated) {
          memo.content = result.updated.content;
          memo.updated_at = new Date().toISOString();
        }
      });
  }

  // 메모 삭제 이벤트
  _onDeleteMenuClick(): void {
    const dialogRef = this._alertService.openConfirm(
      undefined,
      '메모를 삭제하시겠습니까?',
      '삭제하기'
    );

    dialogRef.afterClosed().subscribe((ok) => {
      if (ok) {
        this._annotationsService.deleteMemosById(this._memo!.id).subscribe({
          next: () => {
            this._eventBusService.fire('MemoListComponent:memoDeleted', {
              memoId: this._memo!.id,
            });
            this.close();
          },
          error: (error: APIError) => {
            this._toastService.openWarning(error.message);
          },
        });
      }
    });
  }

  // 메모 내보내기 이벤트
  _onExportMenuClick(memo: Memo): void {
    this._dialogService.open(MemoExportDialogComponent, {
      data: {
        id: memo.id,
        content: memo.content,
      },
    });
  }

  // 메모 추천 이벤트
  _onLikeButtonClick(): void {
    if (this._isLikeRequesting) {
      return;
    }

    if (!this._memo) {
      return;
    }

    this._isLikeRequesting = true;

    const delta = this._memo!.is_liked === 1 ? -1 : 1;

    const request$ =
      delta > 0
        ? this._annotationsService.likeMemo(this._memo!.id)
        : this._annotationsService.unlikeMemo(this._memo!.id);

    request$
      .pipe(
        finalize(() => {
          this._isLikeRequesting = false;
        })
      )
      .subscribe({
        next: () => {
          this._memo!.like_count += delta;
          this._memo!.is_liked = delta > 0 ? 1 : 0;

          this._eventBusService.fire('MemoListComponent:memoUpdated', {
            memoId: this._memo!.id,
            updates: {
              like_count: this._memo!.like_count,
              is_liked: this._memo!.is_liked,
              copy_count: this._memo!.copy_count,
            },
          });

          if (delta === 1) {
            this._toastService.open('메모 추천이 완료되었습니다.');
          }
        },
        error: () => {
          // this._likeCount -= delta;
        },
      });
  }

  // 메모 복사 이벤트
  _onCopyToSingleModeButtonClick(): void {
    if (!this._memo) {
      return;
    }

    this._dialogService
      .open(MemoCopyDialogComponent, {
        data: {
          text: this._memo.text,
          content: this._memo.content,
          author: this._memo.author.nickname,
          memoId: this._memo.id,
        },
      })
      .afterClosed()
      .subscribe((result: any) => {
        if (result) {
          this._memo!.copy_count++;

          this._eventBusService.fire('MemoListComponent:memoUpdated', {
            memoId: this._memo!.id,
            updates: {
              like_count: this._memo!.like_count,
              is_liked: this._memo!.is_liked,
              copy_count: this._memo!.copy_count,
            },
          });
        }
      });
  }

  // 사용자 프로필 클릭 이벤트
  _onAuthorClick(userId: string): void {
    if (!this._memo) {
      return;
    }

    this._readingGroupMembersAPIService
      .getJoined(this._groupId!)
      .subscribe((member) => {
        if (member.find((member) => member.id === userId)) {
          if (this._commentaryId != null) {
            this._dialogService.open(CommentatorDetailsDialogComponent, {
              data: {
                bid: this._bid!,
                avatarImageURL: this._memo!.author.avatar_image_url,
                nickname: this._memo!.author.nickname,
                commentaryId: this._commentaryId,
              },
            });
          } else if (this._groupId != null) {
            this._dialogService
              .open(UserDetailsDialogComponent, {
                data: {
                  bid: this._bid!,
                  userId,
                  isUserBlocked: false,
                  groupId: this._groupId,
                },
              })
              .afterClosed()
              .subscribe((result) => {
                if (result?.userBlocked) {
                  this._eventBusService.fire('UserBlocked:refreshMemoLists');
                  this.close();
                }
              });
          }
        } else {
          this._toastService.open('그룹에 존재하지 않는 그룹원입니다');
        }
      });
  }

  // 메모 신고 이벤트
  _onReportMenuClick(): void {
    this._dialogService
      .open(MemoReportDialogComponent, {
        data: {
          authorName: this._memo!.author.nickname,
          authorAvatarImageURL: this._memo!.author.avatar_image_url,
          memoId: this._memo!.id,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.blocked) {
          this._eventBusService.fire('UserBlocked:refreshMemoLists');
          this.close();
        }
      });
  }

  // 메모 숨기기 이벤트
  _onBlindMenuClick(): void {
    this._alertService
      .openConfirm(
        undefined,
        '메모를 숨김 처리합니다. 숨김 처리 후에는 다시 볼 수 없습니다. 계속하시겠습니까?',
        '계속하기'
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this._annotationsService.blockMemo(this._memo!.id).subscribe(() => {
            this._eventBusService.fire('MemoListComponent:memoDeleted', {
              memoId: this._memo!.id,
            });
            this.close();
          });
        }
      });
  }

  // 댓글 수정 메뉴 클릭 이벤트
  _onEditCommentMenuClick(): void {
    this._selectedCommentForChange = this._selectedCommentForMoreButton!.id;
    this._selectedCommentForMenu = this._selectedCommentForMoreButton;

    if (this._selectedCommentForMenu) {
      this._selectedCommentIdForChange = this._selectedCommentForMenu.id;
      this._changeInputValue = this._selectedCommentForMenu.content;
      this.inputFormType = 'comment-change';
      this._inputValue = this._selectedCommentForMenu.content;
    }
  }

  // 답글 수정 메뉴 클릭 이벤트
  _onEditReplyMenuClick(): void {
    this._selectedCommentForChange = this._selectedCommentForMoreButton!.id;
    this._selectedCommentForMenu = this._selectedCommentForMoreButton;

    this.inputFormType = 'reply-change';
    this._selectedCommentIdForChange = this._selectedCommentForMenu!.comment_id;
    this._selectedReplyIdForChange = this._selectedCommentForMenu!.id;
    this._changeInputValue = this._selectedCommentForMenu!.content;
    this._inputValue = this._selectedCommentForMenu!.content;
  }

  // 사용자 온라인 상태 업데이트
  private _updateAuthorOnlineStatus(): void {
    if (this._memo) {
      this._isAuthorOnline = this._onlineReadingMemberService.isOnline(
        this._memo.author.id
      );
    }
  }

  // 메모 복사 이벤트
  _onCopyMenuClick(memo: Memo): void {
    this._clipboard.copy(createCopyText(memo));
    this._toastService.open('메모가 복사되었습니다.');
  }

  getNicknameIndent(): string {
    if (this.nicknameElement?.nativeElement) {
      // 실제 DOM 요소의 너비를 측정
      const width = this.nicknameElement.nativeElement.offsetWidth + 5;
      return `${width}px`;
    }
    return '0px';
  }

  // 댓글/답글로 스크롤 이동하는 통합 메서드
  private _scrollToElement(elementId: string): void {
    const existingFocusedMemos = document.querySelectorAll('div.focused-memo');
    existingFocusedMemos.forEach((memo) => {
      memo.classList.remove('focused-memo');
    });

    // 해당 요소 찾기
    const element = document.querySelector(
      `div[data-comment-id="${elementId}"]`
    );

    if (element) {
      // 스크롤 가능한 부모 컨테이너 찾기 (.content-container 클래스)
      const scrollContainer = element.closest('.content-container');

      if (scrollContainer) {
        // 컨테이너와 요소의 위치 계산
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // 컨테이너의 현재 스크롤 위치
        const currentScrollTop = scrollContainer.scrollTop;

        // 요소를 컨테이너 중앙에 위치시키기 위한 스크롤 계산
        const elementOffsetFromContainer = elementRect.top - containerRect.top;
        const targetScrollTop =
          currentScrollTop +
          elementOffsetFromContainer -
          containerRect.height / 2 +
          elementRect.height / 2;

        // 부드러운 스크롤로 이동
        scrollContainer.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth',
        });
      } else {
        // 컨테이너를 찾지 못한 경우 기본 동작
        element.scrollIntoView({
          block: 'center',
          inline: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }

  onTextareaFocus(event: FocusEvent): void {
    const textarea = event.target as HTMLTextAreaElement;
    const cancelButton = document.querySelector('.cancel-button');
    cancelButton?.classList.add('focused');
    textarea.classList.add('focused');
  }

  onTextareaBlur(event: FocusEvent): void {
    const textarea = event.target as HTMLTextAreaElement;
    const cancelButton = document.querySelector('.cancel-button');
    cancelButton?.classList.remove('focused');
    textarea.classList.remove('focused');
  }

  // 모든 열린 메뉴를 닫는 메서드
  private _closeAllMenus(): void {
    const backdropClickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      clientX: 0,
      clientY: 0,
    });

    // document.body에 클릭 이벤트를 발생시켜 CDK의 backdrop 클릭 처리 로직을 트리거
    document.body.dispatchEvent(backdropClickEvent);

    // 또는 더 직접적으로 overlay backdrop을 찾아서 클릭 이벤트 발생
    const overlayBackdrop = document.querySelector('.cdk-overlay-backdrop');
    if (overlayBackdrop) {
      overlayBackdrop.dispatchEvent(backdropClickEvent);
    }
  }
}
